import type { ChartSpec } from "@/types";
import { isSavableLiveChart } from "./liveChartContract";

/** Recover only the old, explicit top-N emissions chart format. Other chat
 * transformations still need their own refresh instructions. Never infer
 * scope/date filters from a title: live values follow the Dashboard filters. */
export function restoreLegacyRankedWidget(spec: ChartSpec): ChartSpec {
  const source = spec.dataSource;
  if (
    !source ||
    source.projection !== undefined ||
    Object.keys(source).some((key) => !["source", "params"].includes(key)) ||
    (source.params !== undefined &&
      (!source.params ||
        typeof source.params !== "object" ||
        Array.isArray(source.params) ||
        Object.keys(source.params).length > 0)) ||
    !["emissionsbyunit", "emissionsbyactivity"].includes(source.source) ||
    !["bar", "line", "area", "pie", "donut", "radar", "polarArea"].includes(
      spec.chartType,
    )
  )
    return spec;

  const noun =
    source.source === "emissionsbyunit" ? "facilities" : "activities";
  const title = spec.title?.match(
    /^Top\s+(\d+)\s+(facilities|activities)\s+by\s+(?:approved\s+)?(?:Scope\s+[123]\s+)?emissions(?:\s*\(last\s+\d+\s+(?:full\s+)?months?\))?$/i,
  );
  if (!title || title[2].toLowerCase() !== noun) return spec;
  const limit = Number(title[1]);
  const dataset = spec.data?.datasets?.[0];
  if (
    spec.data?.datasets?.length !== 1 ||
    !dataset ||
    !/^(?:Scope\s+[123]\s+)?emissions(?:\s*\((?:t|kg)CO2e\))?$/i.test(
      dataset.label,
    ) ||
    !["kgCO2e", "tCO2e"].includes(spec.data.unit ?? "") ||
    (spec.data.labels?.length ?? 0) > limit ||
    !Array.isArray(dataset.values) ||
    !dataset.values.every((value) => typeof value === "number" && value >= 0)
  )
    return spec;

  const restored: ChartSpec = {
    ...spec,
    // Remove the old frozen scope/period from the displayed title.
    title: `Top ${limit} ${noun} by emissions`,
    dataSource: {
      source: source.source,
      projection: { version: 1, groupBy: "ranked", limit },
    },
  };
  return isSavableLiveChart(restored) ? restored : spec;
}
